import { createContext, useContext, useEffect, useMemo, useState } from "react";

const CartContext = createContext(null);

const load = () => {
  try {
    return (JSON.parse(localStorage.getItem("kay_cart")) || []).map((i) => ({
      ...i,
      key: i.key || (i.variant ? `${i.id}__${i.variant}` : i.id),
    }));
  } catch {
    return [];
  }
};

export function CartProvider({ children }) {
  const [items, setItems] = useState(load);

  useEffect(() => {
    localStorage.setItem("kay_cart", JSON.stringify(items));
  }, [items]);

  const add = (p, variant = null, qty = 1) => {
    const key = variant ? `${p.id}__${variant.name}` : p.id;
    const stock = variant ? variant.stock : p.stock ?? 99;
    const wanted = Math.max(1, Math.min(qty, stock));
    setItems((prev) => {
      const ex = prev.find((i) => i.key === key);
      if (ex) return prev.map((i) => (i.key === key ? { ...i, qty: Math.min(i.qty + wanted, stock) } : i));
      return [
        ...prev,
        {
          key,
          id: p.id,
          name: p.name,
          brand: p.brand,
          variant: variant?.name || null,
          price: p.price,
          reference_price: p.reference_price || null,
          image: variant?.image || p.images?.[0] || null,
          stock,
          qty: wanted,
        },
      ];
    });
  };

  const setQty = (key, qty) =>
    setItems((prev) => (qty <= 0 ? prev.filter((i) => i.key !== key) : prev.map((i) => (i.key === key ? { ...i, qty } : i))));

  const remove = (key) => setItems((prev) => prev.filter((i) => i.key !== key));
  const clear = () => setItems([]);

  const totals = useMemo(() => {
    const count = items.reduce((s, i) => s + i.qty, 0);
    const subtotal = items.reduce((s, i) => s + i.price * i.qty, 0);
    const savings = items.reduce(
      (s, i) => s + (i.reference_price && i.reference_price > i.price ? (i.reference_price - i.price) * i.qty : 0),
      0
    );
    return { count, subtotal, savings };
  }, [items]);

  return (
    <CartContext.Provider value={{ items, add, setQty, remove, clear, ...totals }}>
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);
